import React, { useState } from 'react';
import { MessageSquare, X, Eye, Clock, User, CheckCircle, Loader } from 'lucide-react';
import { useCustomerNotes } from '../hooks/useCustomerNotes';
import { cn } from '../utils/cn';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast from 'react-hot-toast';

interface PersonNoteBannerProps {
  personId: string;
  customerId: string;
  darkMode?: boolean;
  onViewAllNotes?: () => void; // Callback to open notes tab
}

export const PersonNoteBanner: React.FC<PersonNoteBannerProps> = ({
  personId,
  customerId,
  darkMode = false,
  onViewAllNotes,
}) => {
  const [showConcludeModal, setShowConcludeModal] = useState(false);
  const [concludeReason, setConcludeReason] = useState('');
  const [concludingNoteId, setConcludingNoteId] = useState<string | null>(null);

  // Fetch person-specific notes using useCustomerNotes for concludeNote functionality
  const { notes, concludeNote } = useCustomerNotes(customerId, {
    scope: { person_id: personId },
    activeOnly: true
  });

  // Get the most recent note
  const latestNote = notes.length > 0 ? notes[0] : null;

  const handleConcludeClick = () => {
    if (!latestNote) return;
    setShowConcludeModal(true);
  };

  const handleConcludeNote = async () => {
    if (!latestNote) return;

    setConcludingNoteId(latestNote.id);
    try {
      await concludeNote(latestNote.id, concludeReason.trim() || undefined);
      toast.success('Nota concluída');
      setShowConcludeModal(false);
      setConcludeReason('');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao concluir nota');
    } finally {
      setConcludingNoteId(null);
    }
  };

  // If no notes, don't render
  if (!latestNote) {
    return null;
  }

  return (
    <>
    <div
      className={cn(
        "p-3 rounded-lg border-l-4 flex items-start gap-3",
        darkMode
          ? "bg-amber-900/20 border-amber-600 text-amber-300"
          : "bg-amber-50 border-amber-500 text-amber-900"
      )}
    >
      {/* Icon */}
      <MessageSquare className={cn(
        "w-5 h-5 flex-shrink-0 mt-0.5",
        darkMode ? "text-amber-400" : "text-amber-600"
      )} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <span className={cn(
            "text-xs font-semibold uppercase tracking-wide",
            darkMode ? "text-amber-400" : "text-amber-700"
          )}>
            Nota mais recente
          </span>
          {notes.length > 1 && (
            <span className={cn(
              "text-xs px-1.5 py-0.5 rounded-full",
              darkMode ? "bg-amber-800 text-amber-300" : "bg-amber-200 text-amber-800"
            )}>
              +{notes.length - 1} {notes.length - 1 === 1 ? 'outra' : 'outras'}
            </span>
          )}
        </div>

        {/* Note content */}
        <p className={cn(
          "text-sm line-clamp-2 mb-2",
          darkMode ? "text-amber-200" : "text-amber-900"
        )}>
          {latestNote.content}
        </p>

        {/* Footer */}
        <div className="flex items-center gap-3 text-xs">
          <div className={cn(
            "flex items-center gap-1",
            darkMode ? "text-amber-400" : "text-amber-700"
          )}>
            <User className="w-3 h-3" />
            <span>{latestNote.created_by_name}</span>
          </div>
          <div className={cn(
            "flex items-center gap-1",
            darkMode ? "text-amber-400" : "text-amber-700"
          )}>
            <Clock className="w-3 h-3" />
            <span>{formatDistanceToNow(new Date(latestNote.created_at), { addSuffix: true, locale: ptBR })}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {onViewAllNotes && (
          <button
            onClick={onViewAllNotes}
            className={cn(
              "p-1.5 rounded transition-colors",
              darkMode
                ? "hover:bg-amber-800 text-amber-300 hover:text-amber-100"
                : "hover:bg-amber-200 text-amber-700 hover:text-amber-900"
            )}
            title="Ver todas as notas"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={handleConcludeClick}
          disabled={concludingNoteId !== null}
          className={cn(
            "p-1.5 rounded transition-colors disabled:opacity-50",
            darkMode
              ? "hover:bg-green-800 text-amber-300 hover:text-green-100"
              : "hover:bg-green-200 text-amber-700 hover:text-green-900"
          )}
          title="Concluir nota"
        >
          {concludingNoteId ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>

    {/* Conclude Note Modal */}
    {showConcludeModal && (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
        <div className={cn(
          "w-full max-w-md rounded-lg shadow-xl p-6",
          darkMode ? "bg-gray-800" : "bg-white"
        )}>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className={cn("w-5 h-5", darkMode ? "text-green-400" : "text-green-600")} />
            <h3 className={cn("font-semibold text-lg", darkMode ? "text-gray-100" : "text-gray-900")}>
              Concluir Nota
            </h3>
          </div>

          <p className={cn("text-sm mb-4", darkMode ? "text-gray-400" : "text-gray-600")}>
            Deseja adicionar um motivo para a conclusão desta nota? (opcional)
          </p>

          <textarea
            value={concludeReason}
            onChange={(e) => setConcludeReason(e.target.value)}
            placeholder="Motivo da conclusão (opcional)..."
            rows={3}
            className={cn(
              "w-full px-3 py-2 rounded-lg border resize-none focus:outline-none focus:ring-2 mb-4",
              darkMode
                ? "bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500 focus:ring-green-500"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-green-500"
            )}
          />

          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowConcludeModal(false);
                setConcludeReason('');
              }}
              className={cn(
                "flex-1 px-4 py-2 rounded-lg font-medium transition-colors",
                darkMode
                  ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              )}
            >
              Cancelar
            </button>
            <button
              onClick={handleConcludeNote}
              disabled={concludingNoteId !== null}
              className={cn(
                "flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2",
                darkMode
                  ? "bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-700 disabled:text-gray-500"
                  : "bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300 disabled:text-gray-500"
              )}
            >
              {concludingNoteId ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Concluir
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};
